import {
  CurrencyDollarIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import Select, {
  GroupBase,
  OptionProps,
  SingleValue,
  SingleValueProps,
  StylesConfig,
  components,
} from "react-select";
import { Tooltip } from "react-tooltip";
import InfoTooltip from "../common/InfoTooltip";

export interface OptionType {
  label: string;
  value: any;
  description: string;
  icon?: any;
  isDisabled?: boolean;
  badge?: "new" | "deprecated" | "primary" | "secondary";
  // shown via an info icon next to the badge, e.g. "Use claude-sonnet-5 instead"
  badgeTooltip?: string;
  // shown via a separate "i" icon, e.g. shutdown/retirement date
  infoTooltip?: string;
  // shown via a "$" icon, e.g. cost per 1M input/output tokens
  costTooltip?: string;
}

const BADGE_STYLES: Record<
  NonNullable<OptionType["badge"]>,
  { text: string; className: string }
> = {
  new: { text: "New", className: "bg-blue-100 text-blue-700" },
  deprecated: { text: "Deprecated", className: "bg-gray-100 text-gray-400" },
  primary: { text: "Primary", className: "bg-blue-600 text-white" },
  secondary: { text: "Fallback", className: "bg-primary text-white" },
};

const IconTooltip = ({
  icon: Icon,
  tooltip,
  tooltipId,
}: {
  icon: typeof InformationCircleIcon;
  tooltip?: string;
  tooltipId: string;
}) => {
  if (!tooltip) return null;
  return (
    <>
      <Tooltip
        id={tooltipId}
        place="right"
        delayShow={100}
        style={{
          fontSize: "0.75rem",
          fontWeight: 400,
          lineHeight: "1rem",
          maxWidth: "18rem",
          zIndex: 9999,
          textTransform: "none",
          background: "#000",
        }}
      >
        {tooltip}
      </Tooltip>
      <Icon
        data-tooltip-id={tooltipId}
        data-tooltip-content={tooltip}
        className="size-3.5 ml-1 text-gray-400 hover:text-gray-600"
      />
    </>
  );
};

const Badge = ({
  badge,
  tooltip,
  tooltipId,
}: {
  badge?: OptionType["badge"];
  tooltip?: string;
  tooltipId: string;
}) => {
  if (!badge) return null;
  const { text, className } = BADGE_STYLES[badge];
  return (
    <span className="inline-flex items-center">
      <span
        className={`ml-2 px-1 rounded text-[9px] uppercase font-semibold tracking-wide ${className}`}
      >
        {text}
      </span>
      <IconTooltip
        icon={InformationCircleIcon}
        tooltip={tooltip}
        tooltipId={tooltipId}
      />
    </span>
  );
};

const OptionMeta = ({
  data,
  idPrefix,
}: {
  data: OptionType;
  idPrefix: string;
}) => (
  <span className="inline-flex items-center">
    <Badge
      badge={data.badge}
      tooltip={data.badgeTooltip}
      tooltipId={`${idPrefix}-badge-${String(data.value)}`}
    />
    <IconTooltip
      icon={InformationCircleIcon}
      tooltip={data.infoTooltip}
      tooltipId={`${idPrefix}-info-${String(data.value)}`}
    />
    <IconTooltip
      icon={CurrencyDollarIcon}
      tooltip={data.costTooltip}
      tooltipId={`${idPrefix}-cost-${String(data.value)}`}
    />
  </span>
);

type SelectProps = {
  label: string;
  placeholder: string;
  options: OptionType[] | GroupBase<OptionType>[];
  selected?: SingleValue<OptionType>;
  setSelected: (option: SingleValue<OptionType>) => void;
  tooltip?: string;
  className?: string;
  isClearable?: boolean;
  maxMenuHeight?: number;
  disabled?: boolean;
  showDescriptionBelowLabel?: boolean;
  menuPlacement?: "auto" | "top" | "bottom";
};

const CustomOption =
  (showDescriptionBelowLabel: boolean) => (props: OptionProps<OptionType>) => {
    return (
      <components.Option {...props}>
        <div className="flex items-start px-2">
          {props?.data?.icon && (
            <img
              alt="select-option-icon"
              src={props.data.icon}
              className="w-5 h-5 mr-2"
            />
          )}
          <div className="flex flex-col min-w-[8rem] md:min-w-[12rem]">
            <span className="text-sm flex items-center">
              {props.data.label}
              <OptionMeta
                data={props.data}
                idPrefix="option"
              />
            </span>
            {showDescriptionBelowLabel && props.data.description && (
              <span className="text-xs text-gray-500 leading-4 mt-0.5">
                {props.data.description}
              </span>
            )}
          </div>
          {!showDescriptionBelowLabel && props.data.description && (
            <div className="text-sm text-gray-500">
              {props.data.description}
            </div>
          )}
        </div>
      </components.Option>
    );
  };

const SingleValueComponent = (props: SingleValueProps<OptionType>) => {
  return (
    <components.SingleValue {...props}>
      <div className="flex items-start md:items-center px-0">
        {props?.data?.icon && (
          <img
            alt="select-option-icon"
            src={props.data.icon}
            className="w-5 h-5 mr-2"
          />
        )}
        <div className="text-sm min-w-[8rem] md:min-w-[12rem] flex items-center">
          {props.data.label}
        </div>
      </div>
    </components.SingleValue>
  );
};

const customStyles: StylesConfig<OptionType, false> = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#e5e7eb"
      : state.isFocused
        ? "#f3f4f6"
        : "#fff",
    color: state.isDisabled ? "#9ca3af" : "#1f2937",
    padding: 10,
    cursor: state.isDisabled ? "not-allowed" : "pointer",
  }),
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? "#325BED" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #325BED" : "none",
    cursor: state.isDisabled ? "not-allowed" : "default",
    backgroundColor: state.isDisabled ? "#f3f4f6" : "#fff",
    marginTop: 0,
    borderRadius: 7,
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? "#9ca3af" : "#1f2937",
    fontSize: "0.9rem",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    minWidth: 0,
    overflowX: "hidden",
  }),
  menuList: (base) => ({
    ...base,
    minWidth: 0,
    overflowX: "hidden",
  }),
  groupHeading: (provided) => ({
    ...provided,
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#9ca3af",
    padding: "8px 10px 4px",
  }),
  group: (provided) => ({
    ...provided,
    paddingTop: 4,
    paddingBottom: 4,
  }),
};

const CustomSelect = ({
  label,
  placeholder,
  options,
  selected,
  setSelected,
  tooltip,
  className,
  isClearable = true,
  maxMenuHeight = 300,
  disabled = false,
  showDescriptionBelowLabel = false,
  menuPlacement = "auto",
}: SelectProps) => {
  const handleChange = (option: any) => {
    setSelected(option);
  };

  return (
    <div className="flex flex-col w-full">
      <label
        htmlFor={label.toLowerCase().replace(" ", "-")}
        className="mb-1 flex items-center gap-2 uppercase text-xs tracking-wide leading-6"
      >
        {label}{" "}
        {tooltip && (
          <InfoTooltip
            id={label}
            tooltip={tooltip}
          />
        )}
      </label>
      <div className={className}>
        <Select
          id={label.toLowerCase().replace(" ", "-")}
          placeholder={placeholder}
          value={selected}
          onChange={handleChange}
          options={options}
          components={{
            Option: CustomOption(showDescriptionBelowLabel),
            SingleValue: SingleValueComponent,
          }}
          isSearchable={false}
          menuPortalTarget={document.body}
          styles={customStyles}
          isClearable={isClearable}
          maxMenuHeight={maxMenuHeight}
          menuPlacement={menuPlacement}
          isDisabled={disabled}
        />
      </div>
    </div>
  );
};

export default CustomSelect;
